import { z } from 'zod'
import { COUPLE_FEEDBACK_BODY_MAX, COUPLE_FEEDBACK_NOTE_MAX, REVIEW_LINK_NAME_MAX, REVIEW_LINKS_MAX } from '../constants/couple-feedback.js'
import { COUPLE_FEEDBACK_WORK_STATUSES } from '../enums/couple-feedback-status.enum.js'
import { coupleFeedbackSentimentSchema } from '../enums/couple-feedback-sentiment.enum.js'
import { cleanReviewUrl, isGoogleAddress, isGoogleReviewAddress, reviewLinkProblem } from '../helpers/review-sites.js'
import { PROFILE_URL_MAX } from '../constants/vendor-profile.js'

const httpsMessage = 'Use the full address, starting with https://'

/** Any https address. Ours open in a new tab, so nothing else is offered. */
const httpsUrlSchema = z
  .string()
  .trim()
  .max(PROFILE_URL_MAX, `Keep the address to ${String(PROFILE_URL_MAX)} characters`)
  .url(httpsMessage)
  .refine((value) => {
    try {
      return new URL(value).protocol === 'https:'
    } catch {
      return false
    }
  }, httpsMessage)

/**
 * Where a couple can review the venue in public. Any site will do, but a
 * Google address has to be the write-a-review form, so a search page is never
 * offered as a review, and an address copied from the wrong screen (the
 * venue's own dashboard, a search, a sign-in) is refused with what to use
 * instead. Tracking parameters are dropped from what is saved.
 */
export const reviewLinkUrlSchema = httpsUrlSchema
  .superRefine((value, ctx) => {
    const problem = reviewLinkProblem(value)
    if (problem !== null) {
      ctx.addIssue({ code: 'custom', message: problem })
      return
    }
    if (isGoogleAddress(value) && !isGoogleReviewAddress(value)) {
      ctx.addIssue({ code: 'custom', message: 'Use the Google review address, like https://search.google.com/local/writereview?placeid=…' })
    }
  })
  .transform(cleanReviewUrl)

/** One public review place: what the button calls it, and where it goes. */
export const reviewLinkInputSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Name the site, like Yelp')
      .max(REVIEW_LINK_NAME_MAX, `Keep the name to ${String(REVIEW_LINK_NAME_MAX)} characters`),
    url: reviewLinkUrlSchema,
  })
  .strict()

export type ReviewLinkInput = z.infer<typeof reviewLinkInputSchema>

/**
 * The venue's whole list, in the order the couple sees it: there are few, and
 * the list is what is on screen. An empty list offers no public review; the
 * couple can still send their private note.
 */
export const putVenueReviewLinksInputSchema = z
  .object({
    links: z
      .array(reviewLinkInputSchema)
      .max(REVIEW_LINKS_MAX, `Up to ${String(REVIEW_LINKS_MAX)} review places`)
      .refine((links) => new Set(links.map((link) => link.url)).size === links.length, 'Each address can only be listed once.'),
  })
  .strict()

export type PutVenueReviewLinksInput = z.infer<typeof putVenueReviewLinksInputSchema>

const bodySchema = z
  .string()
  .trim()
  .max(COUPLE_FEEDBACK_BODY_MAX, `Keep it to ${COUPLE_FEEDBACK_BODY_MAX.toLocaleString('en-US')} characters`)

/**
 * The couple's answer. Something that needs attention has to say what.
 * Praise can be the choice alone. The text is stored as they wrote it.
 */
export const submitCoupleFeedbackInputSchema = z
  .object({
    sentiment: coupleFeedbackSentimentSchema,
    body: bodySchema.optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    if (input.sentiment === 'needs_attention' && (input.body === undefined || input.body.length === 0)) {
      ctx.addIssue({ code: 'custom', path: ['body'], message: 'Say what needs attention.' })
    }
  })

export type SubmitCoupleFeedbackInput = z.infer<typeof submitCoupleFeedbackInputSchema>

/** Which of the venue's review places they opened, by its address. */
export const coupleFeedbackClickInputSchema = z
  .object({
    url: z.string().trim().min(1).max(PROFILE_URL_MAX),
  })
  .strict()

export type CoupleFeedbackClickInput = z.infer<typeof coupleFeedbackClickInputSchema>

/** What the venue records on an answer. Praise has no status to change. */
export const updateCoupleFeedbackInputSchema = z
  .object({
    status: z.enum(COUPLE_FEEDBACK_WORK_STATUSES).optional(),
    internalNote: z
      .string()
      .trim()
      .max(COUPLE_FEEDBACK_NOTE_MAX, `Keep it to ${COUPLE_FEEDBACK_NOTE_MAX.toLocaleString('en-US')} characters`)
      .nullable()
      .optional(),
  })
  .strict()
  .refine((input) => input.status !== undefined || input.internalNote !== undefined, 'Change the status or the note.')

export type UpdateCoupleFeedbackInput = z.infer<typeof updateCoupleFeedbackInputSchema>
